import React from 'react';
import { connect } from 'react-redux';
import { setAuthState } from '../slices';
import { Button, Field, TextInput, Icon } from '../components/ui';
import packageData from '../../package.json';
import './../theme/appShell.css';

const apiURL = process.env.REACT_APP_BACKEND;
const version = packageData.version;
const MIN_LEN = 8;

/*
 * ForceChangePassword — shown by App.js immediately after login when the login payload
 * carries changePassword === 'true' (the backend fChangePassword flag, set by an admin
 * temp-password create or reset). The user is already authenticated (we have their token),
 * so this just captures a new password and POSTs it to /api/gateuserupdatepassword, which
 * clears the flag server-side. On success we clear it client-side too and the console opens.
 *
 * Reuses the login card's look (.gw-login/.login-card) so it reads as one continuous flow.
 */
export class ForceChangePassword extends React.Component {
  state = { password: '', confirm: '', error: '', loading: false };

  submit = async (e) => {
    e.preventDefault();
    const password = this.state.password.trim();
    const confirm = this.state.confirm.trim();
    if (password.length < MIN_LEN) {
      this.setState({ error: `Password must be at least ${MIN_LEN} characters.` });
      return;
    }
    if (password !== confirm) {
      this.setState({ error: 'The two passwords do not match.' });
      return;
    }
    this.setState({ error: '', loading: true });
    try {
      const res = await fetch(apiURL + 'api/gateuserupdatepassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': this.props.webToken,
        },
        credentials: 'include',
        body: JSON.stringify({ id: this.props.userId, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.error)) {
        throw new Error((data && data.error) || res.statusText);
      }
      // clear the force-change flag → App re-renders into the console
      await this.props.setAuthState({ field: 'changePassword', value: null });
    } catch (err) {
      console.error('Password change failed:', err);
      this.setState({ loading: false, error: 'Could not set your new password. Please try again.' });
    }
  };

  render() {
    const name = (this.props.firstName || '').trim();
    return (
      <div className="gw-login">
        <form onSubmit={this.submit} className="login-card">
          <div className="brand">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" fill="var(--brand)" />
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11V2Z" fill="#3ea0e0" />
              <path d="M9 12.5 11 15l4-5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Gate</span><b>Watcher</b>
          </div>
          <p className="login-sub">
            {name ? `Welcome, ${name}. ` : ''}Set a new password to continue.
          </p>

          {this.state.error &&
            <div className="login-error" role="alert">
              <Icon name="warning" size={16} title="Error" />
              {this.state.error}
            </div>
          }

          <Field label="New password" htmlFor="newpw" hint={`At least ${MIN_LEN} characters.`}>
            <TextInput
              id="newpw"
              autoComplete="new-password"
              type="password"
              autoFocus
              value={this.state.password}
              onChange={(e) => this.setState({ password: e.target.value })}
            />
          </Field>

          <Field label="Confirm new password" htmlFor="confirmpw">
            <TextInput
              id="confirmpw"
              autoComplete="new-password"
              type="password"
              value={this.state.confirm}
              onChange={(e) => this.setState({ confirm: e.target.value })}
            />
          </Field>

          <Button type="submit" variant="primary" loading={this.state.loading} className="login-submit">
            Set password &amp; continue
          </Button>

          <div className="login-foot">Powered by Dividia Technologies · v{version}</div>
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userId: state.auth.userId,
  webToken: state.auth.webToken,
  firstName: state.auth.firstName,
});
export default connect(mapStateToProps, { setAuthState })(ForceChangePassword);
